import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatCheckboxChange,
  MatCheckboxModule
} from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { I18NextModule } from 'angular-i18next';
import i18next from 'i18next';
import { UserService } from 'src/app/core/services/user.service';
import { FeedbackModalComponent } from 'src/app/shared/components/feedback-modal/feedback-modal.component';
import { ModalConfirmRequestComponent } from 'src/app/shared/components/modal-confirm-request/modal-confirm-request.component';
import {
  CreateRelativeBody,
  RelativeUser
} from 'src/app/shared/model/relative.model';

@Component({
  standalone: true,
  selector: 'app-relatives-vo',
  templateUrl: './relatives-vo.component.html',
  styleUrls: ['./relatives-vo.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    I18NextModule,
    MatCheckboxModule
  ]
})
export class RelativesVoComponent implements OnInit {
  form!: FormGroup;

  relativesList: RelativeUser[] = [];
  spinner = false;

  relationshipOptions: string[] = [
    'step_children',
    'parents_in_law',
    'siblings_in_law',
    'spouse',
    'children',
    'parents',
    'siblings'
  ];

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadRelatives();
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      firstSurName: ['', [Validators.required]],
      secondSurName: ['', [Validators.required]],
      nif: [
        '',
        [Validators.required, Validators.pattern(/^[0-9]{8}[A-Za-z]$/)]
      ],
      email: ['', [Validators.required, Validators.email]],
      relationship: ['', [Validators.required]],
      phoneNumber: ['', [Validators.pattern(/^[0-9]{9}$/)]],
      accessAuthorization: [false, [Validators.requiredTrue]]
    });
  }

  loadRelatives(): void {
    this.spinner = true;

    this.userService.getRelatives().subscribe({
      next: (users) => {
        this.relativesList = users ?? [];
        this.spinner = false;
      },
      error: (error) => {
        console.error('Error loading relatives', error);
        this.relativesList = [];
        this.spinner = false;
      }
    });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const body: CreateRelativeBody = {
      name: this.form.value.name,
      firstSurName: this.form.value.firstSurName,
      secondSurName: this.form.value.secondSurName,
      email: this.form.value.email,
      relationship: this.form.value.relationship,
      nif: this.form.value.nif,
      phoneNumber: this.form.value.phoneNumber
    };

    this.spinner = true;

    this.userService.createRelative(body).subscribe({
      next: () => {
        this.form.reset();
        this.loadRelatives();
        this.dialog.open(FeedbackModalComponent, {
          width: '400px',
          data: {
            text: i18next.t('relativeCreated'),
            close: true,
            theme: 'light'
          }
        });
      },
      error: (error) => {
        console.error('Error creating relative', error);
        this.spinner = false;
        this.dialog.open(FeedbackModalComponent, {
          width: '400px',
          data: {
            text: i18next.t('relativeCreatedError'),
            close: true,
            theme: 'light'
          }
        });
      }
    });
  }

  toggleRelativeStatus(relative: RelativeUser): void {
    this.spinner = true;
    this.userService
      .updateRelativeStatus(relative.relativeId, !relative.status)
      .subscribe({
        next: () => {
          this.loadRelatives();
          this.dialog.open(FeedbackModalComponent, {
            width: '400px',
            data: {
              text: relative.status
                ? i18next.t('deactivateRelative')
                : i18next.t('activateRelative'),
              close: true,
              theme: 'light'
            }
          });
        },
        error: (error) => {
          console.error('Error updating relative status', error);
          this.spinner = false;
          this.dialog.open(FeedbackModalComponent, {
            width: '400px',
            data: {
              text: relative.status
                ? i18next.t('deactivateError')
                : i18next.t('activateError'),
              close: true,
              theme: 'light'
            }
          });
        }
      });
  }

  getStatusLabel(status: boolean): string {
    return status ? 'activeVO' : 'inactiveVO';
  }

  trackByRelative(_: number, item: RelativeUser): string {
    return item.relativeId;
  }

  get f() {
    return this.form.controls;
  }

  onAccessAuthorizationChange(event: MatCheckboxChange): void {
    const control = this.form.get('accessAuthorization');

    if (!event.checked) {
      return;
    }

    const dialogRef = this.dialog.open(ModalConfirmRequestComponent, {
      disableClose: true,
      data: {
        htmlKey: 'voAccessAuthorizationModalHtml',
        confirmText: 'buttonConfirm',
        hideCancelButton: true
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        control?.setValue(false, { emitEvent: false });
      } else {
        control?.markAsTouched();
        control?.updateValueAndValidity();
      }
    });
  }
}
